import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import InlineError from "../../../shared/ui/Field/InlineError";
import { messageFromError } from "../../../shared/api/drfErrors";

import {
  cancelJoinRequest,
  fetchMyJoinRequests,
} from "../api/companyWorkspace";
import { committedEnrollmentStatus } from "../lib/companyLabels";
import { createScopeGate, sameActorId } from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

function CompanyMyJoinRequestsContent() {
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const releaseBusy = (op) => {
    if (busyOpRef.current === op) {
      busyOpRef.current = null;
      setBusyId(null);
    }
  };

  const load = useCallback(({ background = false } = {}) => {
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
    }
    return fetchMyJoinRequests()
      .then((data) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setRows(Array.isArray(data) ? data : []);
        setAuthorityActorId(actorId);
        revalidationConfirmed = true;
        setActionError((current) =>
          current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
        );
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const status = err?.response?.status;
        if (keepVisible && status !== 403 && status !== 404) {
          setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
          return;
        }
        setAuthorityActorId(null);
        setError(messageFromError(err));
        setState("error");
      })
      .finally(() => {
        settleRevalidationWrites({
          keepVisible: keepVisible,
          confirmed: revalidationConfirmed,
          token: backgroundToken,
          tokenAtStart,
          tokenRef: revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        });
      });
  }, [actorId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ actorId, companyId: null });
    busyOpRef.current = null;
    setBusyId(null);
    setActionError(null);
    setRows([]);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: busyId != null,
    onRevalidate: () => load({ background: true }),
  });

  const onCancel = async (row) => {
    if (busyOpRef.current || writesSuspendedRef.current || row.status !== "pending") return;
    const ok = window.confirm(
      `Скасувати заявку до «${row.company?.name || "компанії"}»?`,
    );
    if (!ok) return;
    const op = scopeGate.beginMutation({
      actorId,
      resourceId: row.id,
    });
    busyOpRef.current = op;
    setBusyId(row.id);
    setActionError(null);
    try {
      await cancelJoinRequest(row.company.id, row.id);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const committed = committedEnrollmentStatus(err, "join_request_status");
      setActionError(
        committed
          ? `Заявку вже змінено (${committed}).`
          : messageFromError(err),
      );
      await load();
    } finally {
      releaseBusy(op);
    }
  };

  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);

  if (state === "loading" || (state === "ready" && !actorMatches)) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження заявок" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Помилка"
          description={error || "Спробуйте пізніше."}
          size="md"
        />
      </div>
    );
  }

  if (!actorMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження заявок" />
      </div>
    );
  }

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container">
        <CompanyPageHeader
          title="Мої заявки"
          subtitle="Історія ваших заявок на вступ до компаній."
          actions={
            <Link to="/companies/join-request" className="action-btn">
              Нова заявка
            </Link>
          }
        />
        {actionError ? <InlineError>{actionError}</InlineError> : null}
        {rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-inbox"
            title="Заявок немає"
            description="Подайте заявку через пошук компанії."
            size="md"
          />
        ) : (
          <ul className="feature-company-workspace__list">
            {rows.map((row) => (
              <li key={row.id} className="feature-company-workspace__card">
                <div className="feature-company-workspace__card-top">
                  <div>
                    <h2>{row.company?.name || `Компанія #${row.company?.id}`}</h2>
                    <p>Статус: {row.status}</p>
                  </div>
                  {row.status === "pending" ? (
                    <button
                      type="button"
                      className="action-btn action-btn--danger"
                      disabled={busyId === row.id}
                      onClick={() => onCancel(row)}
                    >
                      Скасувати
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default CompanyMyJoinRequestsContent;
